import React from "react"
import validateInput from "../../../view/shared/validations/validateAmount"
import { WarningMessage } from "../../../view/shared/notifications/Notifications"
import PropTypes from "prop-types"
import { Input, Form, Container, Alert, FormGroup } from "reactstrap"
import { connect } from "react-redux"
import stellarProcessing from "../../../view/assets/stellar-animation.gif"
import { toast } from "react-toastify"
import { formatCurrency } from "../../../view/shared/utilities/outputFormat"
import LastTransaction from "../../reverseTransactions/LastTrasaction"
import { currentTransactionContext } from "../../../store/actions/transactionContext"
import { Trans, useLingui } from "@lingui/react"
import { getCurrency } from "../../../view/shared/utilities/currency"
import { initiateTransaction } from "../../../store/reducers/transactions/actions"
import { selectCreatedVslaInfo } from "../../../store/reducers/getVslas"
import { setCurrentPage } from "../../../store/reducers/navigation"
import styles from "./request.module.css"

const notify = (msg) => toast(msg)

const WelfareRequest = (props) => {
  const { i18n } = useLingui()
  React.useEffect(() => {
    props.pushTransactionContext("Welfare request")
  }, [])

  const [state, setState] = React.useState({
    amount: "",
    errors: {},
    loading: false,
  })

  const onChange = (e) => setState({ ...state, [e.target.name]: e.target.value })

  const isValid = () => {
    const { errors, isValid } = validateInput(state)

    if (!isValid) {
      setState({ ...state, errors })
    }
    return isValid
  }

  const redirectBack = () => {
    props.setPage("group-memberActions")
  }

  const onSubmit = (e) => {
    e.preventDefault()
    if (isValid()) {
      processTransaction(state.amount)
      setState({ ...state, amount: "" })
    }
  }

  const processTransaction = (amount) => {
    const lang_code = i18n.locale

    const lang = {
      fr: {
        message: `Confirmer la cotisation de ${state.amount} pour le membre`,
      },
      en: {
        message: `Confirm contribution of ${state.amount} for member`,
      },
      sw: {
        message: `Thibitisha mwanachama ametoa mchango wa shilingi ${state.amount}`,
      },
      ZARMA: {
        message: `Zarma for Confirm contribution of ${state.amount} for member`,
      },
      HAOUSSA: {
        message: `Tabbatar da taimakon dala ${state.amount} daga ỳar rukuni`,
      },
      es: {
        message: `Confirmar contribución de ${state.amount} lempiras para miembro`,
      },
      lb: {
        message: `Confirm contribution of ${state.amount} for household`,
      },
      pt: {
        message: `Confirme a contribuição de ${state.amount} para o membro`,
      },
      ar: {
        message: `للأسرة ${state.amount} أكد مساهمة`,
      },
    }

    if (window.confirm(lang[lang_code].message)) {
      setState({ ...state, loading: true })
      const uuid = props.currentMember.pubkey
      props.commitTransaction({
        uid: uuid,
        type: "WELFARE_REQUEST",
        amount: parseFloat(amount),
        ...(props.currentMember?.id
          ? {
            member_id: props.currentMember?.id,
          }
          : {
            memberCacheId:
              props.currentMember?.cacheId || props.currentMember?.pubkey,
          }),
      })
      notify(<WarningMessage msg={"Processing Transaction"} />)
      props.setPage("group-memberList")
    } else {
      notify(<WarningMessage />)
    }
  }

  if (!state.loading) {
    return (
      <div>
        <div>
          <h4 className={styles.header}>{props.currentMember?.full_name}</h4>
        </div>
        <div className={styles.header}>
          <Trans id="Welfare Request">Welfare Request</Trans>
        </div>
        <div className={styles.info}>
          <p>
            <Trans id="Total Welfare Contributions">
              Total Welfare Contributions
            </Trans>
            : {formatCurrency(props.welfareBalance?.balance || 0)}{" "}
            {getCurrency(props.group?.country).symbol}
          </p>
        </div>

        {(!props.welfareBalance || props.welfareBalance.balance <= 0) && (
          <Alert color="warning">
            <span>
              <Trans id="Cannot request welfare">Cannot request welfare</Trans>
            </span>
          </Alert>
        )}
        {props.welfareBalance && props.welfareBalance.balance > 0 && (
          <Form onSubmit={onSubmit}>
            <Container>
              <FormGroup>
                <span className={styles.errorMessage}>{state.errors.amount}</span>
                <Input
                  type="number"
                  name="amount"
                  placeholder={i18n._("Welfare Amount")}
                  value={state.amount}
                  onChange={onChange}
                  className={styles.formInput}
                />
              </FormGroup>
              {parseFloat(props.welfareBalance.balance) <
                parseFloat(state.amount) && (
                  <span className={styles.errorMessage}>
                    <Trans id="The amount you are trying to request is more than the group's total welfare">
                      The amount you are trying to request is more than the groups
                      total welfare
                    </Trans>
                  </span>
                )}
              <FormGroup>
                <Input
                  type="submit"
                  value={i18n._("Submit")}
                  name="submit"
                  className={`btn btn-primary ${styles.submitButton}`}
                  disabled={
                    parseFloat(props.welfareBalance.balance) <
                      parseFloat(state.amount)
                      ? true
                      : false
                  }
                />
              </FormGroup>
            </Container>
          </Form>
        )}
        {props.isConnected && (
          <LastTransaction
            redirectBack={() => {
              redirectBack()
            }}
            reversal_details={{
              sourceFundType: "issuer",
              destFundType: "welfare",
              reversalTransactionType: "Welfare request reversed",
            }}
            transactions={props.transactions}
          />
        )}
      </div>
    )
  }

  return (
    <div className={styles.spinnerContainer}>
      <img src={stellarProcessing} className={styles.spinner} alt="Stellar" />
    </div>
  )
}

WelfareRequest.propTypes = {
  vsla: PropTypes.object,
  pushTransactionContext: PropTypes.func,
  setPage: PropTypes.func,
  currentMember: PropTypes.object,
  commitTransaction: PropTypes.func,
  ongoingMeeting: PropTypes.array,
  offlineMeeting: PropTypes.object,
  group: PropTypes.object,
  welfareBalance: PropTypes.array,
  isConnected: PropTypes.bool,
  transactions: PropTypes.array,
}

const mapStateToProps = (state) => {
  const { account, accountV2 } = state

  const selectWelfareBalance = () => {
    if (accountV2.offline) {
      return accountV2.offline.balances.welfare
    }
    return account.balances.welfare
  }

  const selectMemberReversableTransactions = (type) => {
    const memberPubkey = state.loadedMember?.pubkey
    if (!memberPubkey) {
      return []
    }
    else if (!accountV2.online?.transactions) {
      return []
    }
    return accountV2.online?.transactions?.[memberPubkey]?.filter(tx=>tx.type === type) || []
  }
  return {
    isConnected: state.isConnected,
    ongoingMeeting: state.meeting?.ongoing,
    currentMember: state.loadedMember,
    welfareBalance: selectWelfareBalance(),
    transactions: selectMemberReversableTransactions('WELFARE_REQUEST'),
    vsla: state.vsla,
    group: state.group,
    vslaSummary: selectCreatedVslaInfo(state),
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    setPage: (page) => dispatch(setCurrentPage(page)),
    commitTransaction: (data, meeting) =>
      dispatch(initiateTransaction(data, meeting)),
    pushTransactionContext: (type) => dispatch(currentTransactionContext(type)),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(WelfareRequest)
